import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  AlertTriangle, Check, ChevronRight, Folder, Info, Link as LinkIcon, Loader2, Plus,
  RefreshCw, Search, Trash2, Type, Upload, X, FileText,
} from '../../../icons';
import {
  listFontRegistry, uploadFont, deleteFont, registerFontFromUrl, probeFontUrl, setFontFamilyEnabled,
  addFontAlias, deleteFontAlias, fontFileUrl,
  type FontRowFull, type FontAlias, type FontSource,
} from '../../../api/fonts';
import {
  rowFromFile, rowsFromZip, markDuplicates, faceKey, formatBytes, fileToBase64,
  guessFace, FONT_MAX_BYTES,
  type IntakeRow, type IntakeStatus,
} from './fontIntake';
import './UserWorkspace.css';
import './FontWorkspace.css';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

/**
 * 글꼴 관리 — 좌 가족 목록 / 우 미리보기·얼굴 (2026-08-20).
 *
 * 시안 SSOT: docs/font-registry-sample.html
 * 디자인 SSOT: ./UserWorkspace.css 의 `um-*` (사용자 관리·예약관리와 같은 2패널).
 *
 * 옛 화면(FontRegistrySection.tsx — 폼 하나 + 평평한 목록)은 **무수정 보존**한다.
 * 롤백은 ConsolePage.tsx 의 import/렌더 2줄을 되돌리면 끝난다.
 *
 * 이 화면이 옛 화면 대비 실제로 하는 일:
 *   · 등록 경로가 셋이다 — 파일 여러 개 · **압축 폴더**(브라우저에서 푼다) · **주소**(서버가 받는다).
 *   · 목록을 얼굴 낱개가 아니라 **가족**으로 접는다. 12벌 글꼴 하나가 12줄을 먹던 것.
 *   · **빠진 얼굴을 빈 자리로 보여 준다** — 굵게가 없으면 문서 제목이 가짜로 굵어져 줄바꿈이 어긋난다.
 *   · 미리보기를 **등록된 그 파일로** 그린다(잘못 올린 파일이 여기서 바로 드러난다).
 *   · 사용 중 스위치 · 문서가 부르는 다른 이름(별칭) · 출처·등록자 기록.
 *
 * ⚠ 주소 등록·스위치·별칭은 **새 chat-server** 가 있어야 한다. 옛 서버에서는 404 가 오므로
 *   화면이 그걸 알아보고 말로 알려 준다 — 배포 순서에 기대지 않는다.
 */

/** 문서가 흔히 쓰는 얼굴. 이 둘이 없으면 브라우저가 눌러 늘려 그린다(가짜 굵기). */
const EXPECTED_FACES: ReadonlyArray<{ weight: number; style: 'normal' }> = [
  { weight: 400, style: 'normal' },
  { weight: 700, style: 'normal' },
];

const WEIGHT_LABEL: Record<number, string> = {
  100: '아주 가늘게', 200: '가늘게', 300: '가늘게', 400: '보통', 500: '중간',
  600: '조금 굵게', 700: '굵게', 800: '아주 굵게', 900: '가장 굵게',
};
const WEIGHT_OPTIONS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

const SAMPLES: Record<string, [string, string]> = {
  ko: ['다람쥐 헌 쳇바퀴에 타고파', '아무런 준비 없이 맞는 봄은 없다'],
  mix: ['한글 Abc 123 — 회사 표준 글꼴', 'The quick brown fox jumps over the lazy dog'],
  num: ['합계 ₩ 12,480,500', '2026-08-20 · 전표 0031 · 부가세 1,134,591'],
  doc: ['제3조 (계약의 목적)', '갑과 을은 상호 신의성실의 원칙에 따라 다음과 같이 합의한다.'],
};

const SOURCE_LABEL: Record<FontSource, string> = { file: '파일', zip: '압축', url: 'URL' };

/**
 * 미리보기용 내부 글꼴 이름.
 *
 * ⚠ 등록된 이름(`맑은 고딕`)을 그대로 쓰면 **관리자 PC 에 설치된 같은 이름의 글꼴**이 먼저
 *   잡혀, 잘못 올린 파일도 멀쩡해 보인다. 그래서 가족마다 겹치지 않는 이름을 새로 지어
 *   그 이름으로만 그린다 — 화면에 보이는 것은 반드시 **서버에 올라간 그 파일**이다.
 */
function cssFamilyToken(family: string): string {
  let h = 0;
  for (let i = 0; i < family.length; i += 1) h = (h * 31 + family.charCodeAt(i)) >>> 0;
  return `axfont-${h.toString(36)}`;
}

function cssFormatName(format: string): string {
  return format === 'ttf' ? 'truetype' : format === 'otf' ? 'opentype' : format;
}

interface FamilyGroup {
  name: string;
  token: string;
  faces: FontRowFull[];
  aliases: FontAlias[];
  enabled: boolean;
  sources: FontSource[];
  totalBytes: number;
  missing: { weight: number; style: 'normal' }[];
  registeredBy: string | null;
  registeredAt: string | null;
  sourceUrls: string[];
}

function groupFamilies(fonts: FontRowFull[], aliases: FontAlias[]): FamilyGroup[] {
  const byFamily = new Map<string, FontRowFull[]>();
  for (const f of fonts) {
    const list = byFamily.get(f.family) ?? [];
    list.push(f);
    byFamily.set(f.family, list);
  }
  const out: FamilyGroup[] = [];
  for (const [name, faces] of byFamily) {
    faces.sort((a, b) => (a.weight - b.weight) || a.style.localeCompare(b.style));
    const have = new Set(faces.map((f) => `${f.weight}|${f.style}`));
    const newest = faces.reduce<FontRowFull | null>(
      (acc, f) => (!acc || String(f.createdAt ?? '') > String(acc.createdAt ?? '') ? f : acc), null,
    );
    out.push({
      name,
      token: cssFamilyToken(name),
      faces,
      aliases: aliases.filter((a) => a.family === name),
      // 하나라도 켜져 있으면 "사용 중" — 스위치는 가족 통째로 켜고 끈다.
      enabled: faces.some((f) => f.enabled !== false),
      sources: Array.from(new Set(faces.map((f) => f.source ?? 'file'))) as FontSource[],
      totalBytes: faces.reduce((n, f) => n + (f.sizeBytes || 0), 0),
      missing: EXPECTED_FACES.filter((e) => !have.has(`${e.weight}|${e.style}`)),
      registeredBy: newest?.createdByName ?? null,
      registeredAt: newest?.createdAt ? String(newest.createdAt) : null,
      sourceUrls: Array.from(new Set(faces.map((f) => f.sourceUrl).filter(Boolean) as string[])),
    });
  }
  out.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  return out;
}

function whenText(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 서버가 준 오류 문구를 그대로 쓴다 — 없으면 상황에 맞는 말을 만든다. */
function errText(e: any, fallback: string): string {
  const status = e?.response?.status;
  if (status === 404) {
    return '서버가 아직 이 기능을 모릅니다 — 관리 서버(chat-server)를 다시 배포해야 합니다';
  }
  if (status === 413) {
    return `파일이 너무 큽니다 — 한 벌 ${formatBytes(FONT_MAX_BYTES)} 이하만 등록할 수 있습니다 (woff2 를 권합니다)`;
  }
  return e?.response?.data?.message ?? e?.response?.data?.error ?? fallback;
}

export function FontWorkspace() {
  const [fonts, setFonts] = useState<FontRowFull[]>([]);
  const [aliases, setAliases] = useState<FontAlias[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'on' | 'gap' | 'url'>('all');

  const [size, setSize] = useState(26);
  const [weight, setWeight] = useState(400);
  const [sample, setSample] = useState<keyof typeof SAMPLES>('ko');

  const [addOpen, setAddOpen] = useState(false);
  const [aliasDraft, setAliasDraft] = useState('');

  const refresh = useCallback(async (): Promise<FontRowFull[]> => {
    const reg = await listFontRegistry();
    setFonts(reg.fonts);
    setAliases(reg.aliases);
    setError(null);
    return reg.fonts;
  }, []);

  useEffect(() => {
    void refresh()
      .catch((e) => setError(errText(e, '글꼴 목록을 불러오지 못했습니다')))
      .finally(() => setLoading(false));
  }, [refresh]);

  const families = useMemo(() => groupFamilies(fonts, aliases), [fonts, aliases]);

  /* 등록된 글꼴을 **실제로 받아** 그린다 — 목록·미리보기·표가 모두 이 규칙을 쓴다.
   * 문서 <head> 에 style 하나를 두고 목록이 바뀔 때마다 통째로 다시 쓴다(규칙 수가 적다). */
  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-admin-font-preview', '');
    el.textContent = fonts
      .filter((f) => f.id && f.format)
      .map((f) => [
        '@font-face{',
        `font-family:'${cssFamilyToken(f.family)}';`,
        `src:url('${fontFileUrl(f.id)}') format('${cssFormatName(f.format)}');`,
        `font-weight:${f.weight || 400};`,
        `font-style:${f.style === 'italic' ? 'italic' : 'normal'};`,
        'font-display:swap;}',
      ].join(''))
      .join('\n');
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [fonts]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return families.filter((f) => {
      if (filter === 'on' && !f.enabled) return false;
      if (filter === 'gap' && f.missing.length === 0) return false;
      if (filter === 'url' && !f.sources.includes('url')) return false;
      if (q === '') return true;
      return f.name.toLowerCase().includes(q) || f.aliases.some((a) => a.alias.toLowerCase().includes(q));
    });
  }, [families, filter, query]);

  // 선택이 사라졌으면(삭제·검색) 보이는 첫 가족으로 옮긴다 — 오른쪽이 빈 채로 남지 않게.
  useEffect(() => {
    if (visible.length === 0) { setSelectedFamily(null); return; }
    if (!selectedFamily || !visible.some((f) => f.name === selectedFamily)) {
      setSelectedFamily(visible[0]!.name);
      setExpanded((p) => ({ ...p, [visible[0]!.name]: true }));
    }
  }, [visible, selectedFamily]);

  const current = families.find((f) => f.name === selectedFamily) ?? null;
  const totalFaces = families.reduce((n, f) => n + f.faces.length, 0);
  const gapCount = families.filter((f) => f.missing.length > 0).length;
  const onCount = families.filter((f) => f.enabled).length;
  const urlCount = families.filter((f) => f.sources.includes('url')).length;

  async function toggleFamily(fam: FamilyGroup) {
    setBusy(`enable:${fam.name}`);
    setError(null);
    try {
      await setFontFamilyEnabled(fam.name, !fam.enabled);
      await refresh();
      setNotice(fam.enabled
        ? `"${fam.name}" 을 껐습니다 — 파일은 남아 있고 문서만 다시 대체 글꼴로 표시됩니다`
        : `"${fam.name}" 을 켰습니다 — 사용자 화면에는 최대 60초 안에 반영됩니다`);
    } catch (e) {
      setError(errText(e, '사용 중 상태를 바꾸지 못했습니다'));
    } finally { setBusy(null); }
  }

  async function removeFace(face: FontRowFull) {
    const label = `${face.family} ${face.weight}${face.style === 'italic' ? ' 기울임' : ''}`;
    if (!window.confirm(`"${label}" 얼굴 등록을 해제할까요?\n파일이 지워집니다 — 되돌리려면 다시 올려야 합니다.`)) return;
    setBusy(`face:${face.id}`);
    try {
      await deleteFont(face.id);
      await refresh();
    } catch (e) {
      setError(errText(e, '해제하지 못했습니다'));
    } finally { setBusy(null); }
  }

  async function removeFamily(fam: FamilyGroup) {
    if (!window.confirm(
      `"${fam.name}" 을 통째로 해제할까요? (${fam.faces.length}벌)\n`
      + '파일이 모두 지워집니다. 잠시 꺼 두려면 위의 "사용 중" 스위치를 쓰세요.',
    )) return;
    setBusy(`family:${fam.name}`);
    try {
      for (const face of fam.faces) await deleteFont(face.id);
      await refresh();
    } catch (e) {
      setError(errText(e, '해제하지 못했습니다'));
    } finally { setBusy(null); }
  }

  async function submitAlias() {
    const alias = aliasDraft.trim();
    if (!current || alias === '') return;
    setBusy('alias');
    setError(null);
    try {
      await addFontAlias(current.name, alias);
      setAliasDraft('');
      await refresh();
    } catch (e) {
      setError(errText(e, '이름을 추가하지 못했습니다'));
    } finally { setBusy(null); }
  }

  async function removeAlias(a: FontAlias) {
    setBusy(`alias:${a.id}`);
    try {
      await deleteFontAlias(a.id);
      await refresh();
    } catch (e) {
      setError(errText(e, '이름을 지우지 못했습니다'));
    } finally { setBusy(null); }
  }

  /** 주소로 들인 글꼴을 배포처에서 다시 받아 온다(배포처가 갱신했을 때). */
  async function resync(fam: FamilyGroup) {
    setBusy(`resync:${fam.name}`);
    setError(null);
    let ok = 0;
    try {
      for (const face of fam.faces) {
        if (!face.sourceUrl) continue;
        await registerFontFromUrl({
          url: face.sourceUrl, family: face.family, weight: face.weight,
          style: face.style === 'italic' ? 'italic' : 'normal',
        });
        ok += 1;
      }
      await refresh();
      setNotice(`${ok}벌을 배포처에서 다시 받았습니다`);
    } catch (e) {
      setError(errText(e, '다시 가져오지 못했습니다'));
    } finally { setBusy(null); }
  }

  const [l1, l2] = SAMPLES[sample]!;
  const previewStyle: CSSProperties = current
    ? { fontFamily: `'${current.token}', sans-serif`, fontWeight: weight, fontSize: `${size}px` }
    : {};
  const currentFace = current?.faces.find((f) => f.weight === weight && f.style === 'normal') ?? null;

  return (
    <>
      {/* 화면 머리 — 종전에는 좌측 패널 머리(um-ph)뿐이라 화면 제목이 없었다. */}
      <ConsoleScreenHead
        title="글꼴 관리"
        kpi={`${families.length}가족 · ${totalFaces}벌`}
        desc="문서·편집기·미리보기가 받아 쓰는 글꼴을 회사가 등록합니다."
      />
    <section className="um-ws fw-ws">
      {/* ── 좌: 글꼴 가족 ───────────────────────────────────────── */}
      <div className="um-panel">
        <div className="um-ph">
          <h3>등록 글꼴</h3>
          <span className="um-ph__n">{families.length}가족 · {totalFaces}벌</span>
          <span className="um-ph__sp" />
        </div>

        <div className="um-org__tools">
          <button type="button" className="um-btn um-btn--primary" onClick={() => setAddOpen(true)}>
            <Plus size={15} /> 글꼴 추가
          </button>
          <div className="um-search">
            <Search size={15} />
            <input
              type="search"
              value={query}
              placeholder="글꼴 이름 · 별칭 검색"
              aria-label="글꼴 검색"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="fw-filters" role="group" aria-label="목록 필터">
            {([
              ['all', '전체', families.length],
              ['on', '사용 중', onCount],
              ['gap', '빠진 얼굴', gapCount],
              ['url', 'URL', urlCount],
            ] as const).map(([key, label, n]) => (
              <button
                key={key}
                type="button"
                className={`fw-chip${filter === key ? ' is-on' : ''}`}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              >
                {label} <span className="fw-chip__n">{n}</span>
              </button>
            ))}
          </div>
        </div>

        <nav className="um-tree" aria-label="글꼴 가족">
          {loading && <p className="fw-empty">불러오는 중…</p>}
          {!loading && families.length === 0 && (
            <p className="fw-empty">
              등록된 글꼴이 없습니다 — 문서는 브라우저의 대체 글꼴로 표시됩니다.
              <br />위 <b>글꼴 추가</b> 로 파일·압축 폴더·주소 중 편한 것으로 들이세요.
            </p>
          )}
          {!loading && families.length > 0 && visible.length === 0 && (
            <p className="fw-empty">조건에 맞는 글꼴이 없습니다.</p>
          )}

          {visible.map((fam) => {
            const isOpen = expanded[fam.name] === true;
            const sel = fam.name === selectedFamily;
            return (
              <div key={fam.name}>
                <div
                  className={`um-node fw-node${sel ? ' is-sel' : ''}${fam.enabled ? '' : ' fw-node--off'}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setSelectedFamily(fam.name); setExpanded((p) => ({ ...p, [fam.name]: true })); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedFamily(fam.name);
                      setExpanded((p) => ({ ...p, [fam.name]: true }));
                    }
                  }}
                >
                  <span
                    className={`um-tw${isOpen ? ' is-open' : ''}`}
                    role="button"
                    tabIndex={-1}
                    aria-label={isOpen ? '접기' : '펼치기'}
                    onClick={(e) => { e.stopPropagation(); setExpanded((p) => ({ ...p, [fam.name]: !isOpen })); }}
                  >
                    <ChevronRight size={13} />
                  </span>
                  {/* 등록된 그 파일로 그린다 — 잘못 올린 파일은 여기서 바로 드러난다. */}
                  <span className="fw-ag" style={{ fontFamily: `'${fam.token}', sans-serif` }}>Ag</span>
                  <span className="fw-fam">
                    <span className="fw-fam__nm" style={{ fontFamily: `'${fam.token}', inherit` }}>{fam.name}</span>
                    <span className="fw-fam__s">
                      {fam.faces.length}벌
                      {fam.missing.length > 0 && ` · 빠짐 ${fam.missing.length}`}
                      {!fam.enabled && ' · 꺼짐'}
                    </span>
                  </span>
                  <span className={`fw-src fw-src--${fam.enabled ? (fam.sources[0] ?? 'file') : 'off'}`}>
                    {SOURCE_LABEL[fam.sources[0] ?? 'file']}
                  </span>
                </div>

                {isOpen && (
                  <div className="um-kids">
                    {fam.faces.map((f) => (
                      <div key={f.id} className="fw-face">
                        <span className="fw-face__w">{f.weight}</span>
                        <span
                          className="fw-face__nm"
                          style={{
                            fontFamily: `'${fam.token}', inherit`,
                            fontWeight: f.weight,
                            fontStyle: f.style === 'italic' ? 'italic' : 'normal',
                          }}
                        >
                          {WEIGHT_LABEL[f.weight] ?? f.weight}{f.style === 'italic' ? ' · 기울임' : ''}
                        </span>
                        <span className="fw-face__sz">{formatBytes(f.sizeBytes)}</span>
                      </div>
                    ))}
                    {fam.missing.map((m) => (
                      <div key={`miss-${m.weight}`} className="fw-face fw-face--missing">
                        <span className="fw-face__w">{m.weight}</span>
                        <span className="fw-face__nm">{WEIGHT_LABEL[m.weight]} — 없음</span>
                        <button
                          type="button"
                          className="fw-face__add"
                          onClick={(e) => { e.stopPropagation(); setSelectedFamily(fam.name); setAddOpen(true); }}
                        >
                          추가
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* ── 우: 상세 ────────────────────────────────────────────── */}
      <div className="um-panel">
        <div className="um-ph">
          <span className="um-crumb">
            글꼴 <ChevronRight size={13} />
            <b style={current ? { fontFamily: `'${current.token}', inherit` } : undefined}>
              {current?.name ?? '—'}
            </b>
          </span>
          <span className="um-ph__sp" />
          {current && (
            <>
              <button
                type="button"
                className={`fw-sw${current.enabled ? ' is-on' : ''}`}
                aria-pressed={current.enabled}
                disabled={busy === `enable:${current.name}`}
                onClick={() => void toggleFamily(current)}
                title="끄면 파일은 남고 문서에서만 빠집니다"
              >
                <span className="fw-sw__t" />
                {current.enabled ? '사용 중' : '꺼짐'}
              </button>
              <button
                type="button"
                className="um-btn um-btn--sm um-btn--danger"
                disabled={busy === `family:${current.name}`}
                onClick={() => void removeFamily(current)}
              >
                <Trash2 size={14} /> 가족 전체 해제
              </button>
            </>
          )}
        </div>

        <div className="fw-body">
          {error && (
            <div className="fw-note fw-note--bad" role="alert">
              <AlertTriangle size={15} />
              <span>{error}</span>
            </div>
          )}
          {notice && !error && (
            <div className="fw-note fw-note--info">
              <Info size={15} />
              <span>{notice}</span>
              <button type="button" className="fw-note__x" aria-label="닫기" onClick={() => setNotice(null)}>
                <X size={13} />
              </button>
            </div>
          )}

          {!current && !loading && (
            <p className="fw-empty">왼쪽에서 글꼴을 고르면 미리보기와 얼굴 목록이 나옵니다.</p>
          )}

          {current && (
            <>
              {/* 미리보기 */}
              <div className="fw-prev">
                <div className="fw-prev__bar">
                  <span className="fw-prev__lab">미리보기</span>
                  <div className="fw-filters" role="group" aria-label="미리보기 크기">
                    {[14, 18, 26, 38].map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`fw-chip${size === s ? ' is-on' : ''}`}
                        aria-pressed={size === s}
                        onClick={() => setSize(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <div className="fw-filters" role="group" aria-label="미리보기 굵기">
                    {[400, 700, 800].map((w) => {
                      const has = current.faces.some((f) => f.weight === w);
                      return (
                        <button
                          key={w}
                          type="button"
                          className={`fw-chip${weight === w ? ' is-on' : ''}${has ? '' : ' fw-chip--absent'}`}
                          aria-pressed={weight === w}
                          title={has ? '' : '등록되지 않은 굵기 — 브라우저가 눌러 늘려 그립니다'}
                          onClick={() => setWeight(w)}
                        >
                          {WEIGHT_LABEL[w]}
                        </button>
                      );
                    })}
                  </div>
                  <span className="fw-prev__sp" />
                  <div className="um-selw">
                    <select
                      className="um-inp fw-sel"
                      value={sample}
                      aria-label="샘플 문구"
                      onChange={(e) => setSample(e.target.value as keyof typeof SAMPLES)}
                    >
                      <option value="ko">한글 문장</option>
                      <option value="mix">한글 + 영문 + 숫자</option>
                      <option value="num">표 · 금액</option>
                      <option value="doc">문서 첫 줄</option>
                    </select>
                    <ChevronRight size={13} className="fw-sel__cv" />
                  </div>
                </div>
                <div className="fw-prev__body">
                  <div className="fw-prev__line" style={previewStyle}>{l1}</div>
                  <div
                    className="fw-prev__line fw-prev__line--sub"
                    style={{ ...previewStyle, fontSize: `${Math.max(13, Math.round(size * 0.62))}px` }}
                  >
                    {l2}
                  </div>
                  <div className="fw-prev__meta">
                    {currentFace
                      ? `${current.name} · ${currentFace.weight} · ${currentFace.format} · ${formatBytes(currentFace.sizeBytes)}`
                      : `${current.name} · ${weight} — 이 굵기는 등록돼 있지 않습니다 (브라우저가 눌러 그림)`}
                  </div>
                </div>
              </div>

              {current.missing.length > 0 && (
                <div className="fw-note">
                  <AlertTriangle size={15} />
                  <span>
                    <b>{current.missing.map((m) => WEIGHT_LABEL[m.weight]).join(' · ')}</b> 얼굴이 없습니다.
                    문서가 이 얼굴을 부르면 브라우저가 있는 얼굴을 눌러 늘려 그려, 줄바꿈이 원본과 어긋납니다.
                  </span>
                </div>
              )}
              {!current.enabled && (
                <div className="fw-note fw-note--info">
                  <Info size={15} />
                  <span>
                    이 글꼴은 <b>꺼져 있습니다</b> — 파일은 남아 있고 사용자 화면의 글꼴 목록에서만 빠집니다.
                    스위치를 켜면 최대 60초 안에 반영됩니다.
                  </span>
                </div>
              )}

              {/* 얼굴 표 */}
              <div className="fw-sec">
                <div className="fw-sec__h">
                  <h4>얼굴</h4>
                  <span className="fw-sec__n">
                    {current.faces.length}벌{current.missing.length > 0 && ` · 빠짐 ${current.missing.length}`}
                  </span>
                  <span className="fw-sec__sp" />
                  <button type="button" className="um-btn um-btn--sm" onClick={() => setAddOpen(true)}>
                    <Plus size={14} /> 이 가족에 얼굴 추가
                  </button>
                </div>
                <p className="fw-sec__d">
                  굵기·기울임은 <b>파일이 각각 따로</b>입니다. 없는 얼굴은 브라우저가 있는 얼굴을 눌러
                  늘려 그리므로(가짜 굵기·가짜 기울임) 문서의 줄바꿈이 원본과 어긋납니다.
                </p>
                <div className="fw-tw">
                  <table className="fw-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 74 }}>굵기</th>
                        <th style={{ width: 66 }}>기울임</th>
                        <th>미리보기</th>
                        <th style={{ width: 70 }}>형식</th>
                        <th style={{ width: 80 }}>용량</th>
                        <th style={{ width: 92 }}>출처</th>
                        <th style={{ width: 118 }}>등록</th>
                        <th style={{ width: 78 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {current.faces.map((f) => (
                        <tr key={f.id}>
                          <td className="num">{f.weight}</td>
                          <td>{f.style === 'italic' ? '기울임' : '보통'}</td>
                          <td>
                            <span
                              className="fw-samp"
                              style={{
                                fontFamily: `'${current.token}', sans-serif`,
                                fontWeight: f.weight,
                                fontStyle: f.style === 'italic' ? 'italic' : 'normal',
                              }}
                            >
                              가나다 Abc 123
                            </span>
                          </td>
                          <td className="num">{f.format}</td>
                          <td className="num">{formatBytes(f.sizeBytes)}</td>
                          <td>
                            <span className={`fw-src fw-src--${f.source ?? 'file'}`}>
                              {SOURCE_LABEL[f.source ?? 'file']}
                            </span>
                          </td>
                          <td className="num">{whenText(f.createdAt ? String(f.createdAt) : null)}</td>
                          <td>
                            <div className="fw-tbl__act">
                              <button
                                type="button"
                                className="um-btn um-btn--sm um-btn--danger"
                                disabled={busy === `face:${f.id}`}
                                onClick={() => void removeFace(f)}
                                aria-label={`${f.family} ${f.weight} 해제`}
                              >
                                {busy === `face:${f.id}` ? <Loader2 size={13} className="fw-spin" /> : <Trash2 size={13} />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {current.missing.map((m) => (
                        <tr key={`miss-${m.weight}`} className="is-missing">
                          <td className="num">{m.weight}</td>
                          <td>보통</td>
                          <td colSpan={5} className="fw-missing">
                            등록되지 않음 — 문서가 부르면 가짜로 그려집니다
                          </td>
                          <td>
                            <div className="fw-tbl__act">
                              <button type="button" className="um-btn um-btn--sm" onClick={() => setAddOpen(true)}>
                                올리기
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 별칭 */}
              <div className="fw-sec">
                <div className="fw-sec__h">
                  <h4>문서가 부르는 다른 이름</h4>
                  <span className="fw-sec__n">{current.aliases.length}개</span>
                </div>
                <p className="fw-sec__d">
                  글꼴을 찾는 열쇠는 <b>문서 안에 적힌 이름</b>입니다. 같은 글꼴이라도 문서마다
                  <code>NanumGothic</code> · <code>Nanum Gothic</code> 처럼 다르게 적혀 있어, 이름이 한 글자만
                  달라도 대체되지 않습니다. 여기 적어 둔 이름은 모두 이 파일로 이어집니다.
                </p>
                <div className="fw-chips">
                  {current.aliases.map((a) => (
                    <span key={a.id} className="fw-tag">
                      {a.alias}
                      <button
                        type="button"
                        aria-label={`${a.alias} 제거`}
                        disabled={busy === `alias:${a.id}`}
                        onClick={() => void removeAlias(a)}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  <span className="fw-tag fw-tag--add">
                    <input
                      className="fw-tag__inp"
                      value={aliasDraft}
                      maxLength={80}
                      placeholder="이름 추가"
                      aria-label="문서가 부르는 다른 이름 추가"
                      onChange={(e) => setAliasDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submitAlias(); } }}
                    />
                    <button
                      type="button"
                      aria-label="이름 추가"
                      disabled={aliasDraft.trim() === '' || busy === 'alias'}
                      onClick={() => void submitAlias()}
                    >
                      {busy === 'alias' ? <Loader2 size={12} className="fw-spin" /> : <Check size={12} />}
                    </button>
                  </span>
                </div>
              </div>

              {/* 등록 정보 */}
              <div className="fw-sec">
                <div className="fw-sec__h"><h4>등록 정보</h4></div>
                <dl className="fw-meta">
                  <div>
                    <dt>출처</dt>
                    <dd className={current.sourceUrls.length ? 'mono' : undefined}>
                      {current.sourceUrls[0] ?? current.sources.map((s) => SOURCE_LABEL[s]).join(' · ')}
                    </dd>
                  </div>
                  <div>
                    <dt>등록</dt>
                    <dd>
                      {current.registeredBy ?? '기록 없음'}
                      <br />
                      <span className="mono fw-meta__sub">{whenText(current.registeredAt)}</span>
                    </dd>
                  </div>
                  <div>
                    <dt>합계 용량</dt>
                    <dd className="mono">{formatBytes(current.totalBytes)} · {current.faces.length}개 파일</dd>
                  </div>
                  <div>
                    <dt>사용자 화면 반영</dt>
                    <dd>fonts.css · 최대 60초</dd>
                  </div>
                  {current.sourceUrls.length > 0 && (
                    <div>
                      <dt>배포처 갱신</dt>
                      <dd>
                        <button
                          type="button"
                          className="um-btn um-btn--sm"
                          disabled={busy === `resync:${current.name}`}
                          onClick={() => void resync(current)}
                        >
                          {busy === `resync:${current.name}`
                            ? <Loader2 size={13} className="fw-spin" />
                            : <RefreshCw size={13} />}
                          지금 다시 가져오기
                        </button>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </>
          )}
        </div>
      </div>

      {addOpen && (
        <FontAddDialog
          existing={fonts}
          presetFamily={current?.name ?? ''}
          onClose={() => setAddOpen(false)}
          onDone={async (added) => {
            const next = await refresh().catch(() => null);
            if (next && added > 0) setNotice(`${added}벌 등록 완료 — 사용자 화면에는 최대 60초 안에 반영됩니다`);
          }}
        />
      )}
    </section>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   글꼴 추가 — 파일 · 압축 폴더 · 주소를 한 창의 세 탭으로.

   셋이 서로 다른 화면이 아니라 같은 창의 탭인 이유는, 어느 쪽으로 넣든 **마지막 단계가
   같기 때문**이다 — 이름·굵기·기울임을 확인하고 등록. 확인 표를 공유하면 압축 30벌도
   파일 한 개와 같은 방식으로 다룬다.
   ══════════════════════════════════════════════════════════════════════════ */

type AddTab = 'file' | 'zip' | 'url';

const PILL: Record<IntakeStatus, { cls: string; label: string }> = {
  new: { cls: 'fw-pill--new', label: '신규' },
  replace: { cls: 'fw-pill--rep', label: '교체' },
  dupe: { cls: 'fw-pill--skip', label: '중복' },
  skip: { cls: 'fw-pill--skip', label: '건너뜀' },
  bad: { cls: 'fw-pill--bad', label: '형식 오류' },
  big: { cls: 'fw-pill--bad', label: '용량 초과' },
  denied: { cls: 'fw-pill--bad', label: '주소 거절' },
  unreachable: { cls: 'fw-pill--bad', label: '받지 못함' },
  failed: { cls: 'fw-pill--bad', label: '실패' },
  done: { cls: 'fw-pill--new', label: '등록됨' },
};

/** 올릴 수 있는 줄인가 — 신규·교체만 올린다. */
function isSendable(r: IntakeRow): boolean {
  return r.status === 'new' || r.status === 'replace';
}

function FontAddDialog(props: {
  existing: FontRowFull[];
  presetFamily: string;
  onClose: () => void;
  onDone: (added: number) => void | Promise<void>;
}) {
  const { existing, presetFamily, onClose, onDone } = props;
  const [tab, setTab] = useState<AddTab>('file');
  const [rows, setRows] = useState<IntakeRow[]>([]);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [reading, setReading] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [urlText, setUrlText] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);

  const existingKeys = useMemo(
    () => new Set(existing.map((f) => faceKey(f.family, f.weight, f.style))),
    [existing],
  );

  /** 이미 있는 얼굴이면 '교체' 로 바꿔 둔다 — 덮어쓰기를 조용히 하지 않는다. */
  const withReplace = useCallback((list: IntakeRow[]): IntakeRow[] => list.map((r) => {
    if (r.status !== 'new') return r;
    return existingKeys.has(faceKey(r.family, r.weight, r.style))
      ? { ...r, status: 'replace' as IntakeStatus }
      : r;
  }), [existingKeys]);

  function setAll(list: IntakeRow[]) {
    const marked = withReplace(markDuplicates(list));
    setRows(marked);
    const next: Record<string, boolean> = {};
    for (const r of marked) next[r.key] = isSendable(r);
    setPicked(next);
    setDone(0);
  }

  async function takeFiles(files: File[]) {
    setErr(null);
    setReading(true);
    try {
      const zips = files.filter((f) => /\.zip$/i.test(f.name));
      const plain = files.filter((f) => !/\.zip$/i.test(f.name));
      const out: IntakeRow[] = [];
      for (let i = 0; i < plain.length; i += 1) out.push(await rowFromFile(plain[i]!, i));
      for (const z of zips) out.push(...await rowsFromZip(z));
      setAll([...rows, ...out]);
    } catch (e) {
      setErr('압축을 풀지 못했습니다 — 손상되었거나 암호가 걸린 파일로 보입니다');
    } finally {
      setReading(false);
    }
  }

  function patchRow(key: string, patch: Partial<IntakeRow>) {
    setRows((prev) => withReplace(markDuplicates(prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))));
  }

  /**
   * 주소 확인 — 서버가 받아 형식·용량만 알려 준다(**저장하지 않는다**).
   * 배포처가 준 것이 `@font-face` CSS 면 그 안의 글꼴 주소로 **펼쳐서** 표에 올린다.
   */
  async function probeUrls() {
    const urls = urlText.split(/\s+/).map((s) => s.trim()).filter((s) => s !== '');
    if (urls.length === 0) return;
    setErr(null);
    setReading(true);
    const out: IntakeRow[] = [];
    let i = 0;
    for (const url of urls) {
      i += 1;
      const base: IntakeRow = {
        key: `u${i}-${url}`, path: '', name: url, url,
        family: '', weight: 400, style: 'normal', format: null, sizeBytes: 0, status: 'new',
      };
      try {
        const res = await probeFontUrl(url);
        if (res.kind === 'expanded') {
          let j = 0;
          for (const f of res.expanded) {
            j += 1;
            const g = guessFace(f.url);
            out.push({
              ...base,
              key: `u${i}-${j}-${f.url}`,
              name: f.url,
              url: f.url,
              family: (f.family ?? g.family).trim(),
              weight: f.weight ?? g.weight,
              style: f.style ?? g.style,
            });
          }
        } else {
          const g = guessFace(url);
          out.push({
            ...base,
            family: (res.probe.family || g.family).trim(),
            weight: g.weight,
            style: g.style,
            format: res.probe.format as IntakeRow['format'],
            sizeBytes: res.probe.sizeBytes,
          });
        }
      } catch (e: any) {
        // 사유를 갈라 준다 — 주소를 고쳐야 하는 것과 파일이 글꼴이 아닌 것은 할 일이 다르다.
        const code = e?.response?.data?.code;
        const status: IntakeStatus = code === 'BLOCKED_HOST' || code === 'INVALID_URL' ? 'denied'
          : code === 'UNSUPPORTED_MEDIA_TYPE' ? 'bad'
          : code === 'TOO_LARGE' ? 'big'
          : 'unreachable';
        out.push({ ...base, status, reason: errText(e, '주소에서 받지 못했습니다') });
      }
    }
    setAll([...rows, ...out]);
    setReading(false);
  }

  async function run() {
    setRunning(true);
    setErr(null);
    let ok = 0;
    for (const row of rows) {
      if (!picked[row.key] || !isSendable(row)) continue;
      try {
        if (row.url) {
          await registerFontFromUrl({
            url: row.url, family: row.family.trim(), weight: row.weight, style: row.style,
          });
        } else if (row.file) {
          await uploadFont({
            family: row.family.trim(),
            weight: row.weight,
            style: row.style,
            data: await fileToBase64(row.file),
            // 압축에서 나온 줄은 키가 z 로 시작한다 — 목록에서 출처를 갈라 보여 주려는 것.
            source: row.key.startsWith('z') ? 'zip' : 'file',
          });
        }
        ok += 1;
        setDone(ok);
        setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, status: 'done' } : r)));
      } catch (e) {
        setRows((prev) => prev.map((r) => (
          r.key === row.key ? { ...r, status: 'failed', reason: errText(e, '등록하지 못했습니다') } : r
        )));
      }
    }
    setRunning(false);
    await onDone(ok);
    // 전부 됐으면 닫는다. 실패가 남았으면 표를 그대로 두어 다시 시도할 수 있게 한다.
    const remaining = rows.filter((r) => picked[r.key] && isSendable(r)).length;
    if (ok === remaining && ok > 0) onClose();
  }

  const sendable = rows.filter((r) => picked[r.key] && isSendable(r));
  const excluded = rows.length - sendable.length;
  const replaceN = sendable.filter((r) => r.status === 'replace').length;

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    void takeFiles(Array.from(e.dataTransfer.files ?? []));
  }

  return (
    <div
      className="fw-mask"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !running) onClose(); }}
    >
      <div className="fw-modal" role="dialog" aria-modal="true" aria-labelledby="fw-add-title">
        <div className="fw-modal__h">
          <Type size={17} className="fw-modal__icon" />
          <h3 id="fw-add-title">글꼴 추가</h3>
          <span className="fw-modal__sp" />
          <button type="button" className="fw-modal__x" aria-label="닫기" disabled={running} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="fw-tabs" role="tablist" aria-label="등록 방법">
          {([
            ['file', '파일', <FileText key="i" size={15} />],
            ['zip', '압축 폴더', <Folder key="i" size={15} />],
            ['url', 'URL', <LinkIcon key="i" size={15} />],
          ] as const).map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`fw-tab${tab === key ? ' is-on' : ''}`}
              onClick={() => setTab(key)}
            >
              {icon}{label}
            </button>
          ))}
        </div>
        <div className="fw-tabline" />

        <div className="fw-modal__b">
          {err && (
            <div className="fw-note fw-note--bad" role="alert">
              <AlertTriangle size={15} /><span>{err}</span>
            </div>
          )}

          {tab === 'file' && (
            <>
              <div
                className={`fw-drop${dragOver ? ' is-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
              >
                <Upload size={26} />
                <b>글꼴 파일을 끌어다 놓으세요</b>
                <small>
                  woff2 · woff · ttf · otf — 한 벌 {formatBytes(FONT_MAX_BYTES)} 이하, 여러 개를 한 번에 놓아도 됩니다.
                  <br />같은 글꼴이면 <b>woff2</b> 가 가장 작아 화면이 빨리 뜹니다.
                </small>
                <button type="button" className="um-btn um-btn--sm" onClick={() => fileRef.current?.click()}>
                  파일 고르기
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  hidden
                  accept=".woff2,.woff,.ttf,.otf,.ttc"
                  onChange={(e) => void takeFiles(Array.from(e.target.files ?? []))}
                />
              </div>
              <div className="fw-note fw-note--info">
                <Info size={15} />
                <span>
                  <b>글꼴 이름이 열쇠입니다.</b> 문서 안에 적힌 이름과 <b>똑같아야</b> 그 글꼴로 그려집니다.
                  문서마다 이름이 다르게 적혀 있다면, 등록 뒤 상세 화면의
                  <b> 문서가 부르는 다른 이름</b>에 함께 적어 두세요.
                </span>
              </div>
            </>
          )}

          {tab === 'zip' && (
            <>
              <div
                className={`fw-drop${dragOver ? ' is-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
              >
                <Folder size={26} />
                <b>압축 폴더(zip)를 끌어다 놓으세요</b>
                <small>
                  배포처가 준 압축을 그대로 놓으면 됩니다 — 하위 폴더까지 훑어 글꼴 파일만 고릅니다.
                  <br />압축은 <b>이 브라우저에서 풀립니다.</b> 서버로는 아래에서 확인한 것만 올라갑니다.
                </small>
                <button type="button" className="um-btn um-btn--sm" onClick={() => zipRef.current?.click()}>
                  압축 파일 고르기
                </button>
                <input
                  ref={zipRef}
                  type="file"
                  hidden
                  accept=".zip,application/zip"
                  onChange={(e) => void takeFiles(Array.from(e.target.files ?? []))}
                />
              </div>
              {rows.some((r) => r.status === 'dupe') && (
                <div className="fw-note">
                  <AlertTriangle size={15} />
                  <span>
                    같은 얼굴이 형식만 달리 여럿 들어 있습니다 — <b>작은 쪽(woff2)</b>만 남기고 나머지는
                    중복으로 표시했습니다. 필요하면 체크를 직접 켜세요.
                  </span>
                </div>
              )}
            </>
          )}

          {tab === 'url' && (
            <>
              <div className="fw-f">
                <label htmlFor="fw-urls">글꼴 주소 — 한 줄에 하나</label>
                <textarea
                  id="fw-urls"
                  className="um-inp fw-urlbox"
                  rows={4}
                  spellCheck={false}
                  value={urlText}
                  placeholder={'https://fonts.example.co.kr/kr/Pretendard-Regular.woff2\nhttps://fonts.example.co.kr/kr/font.css'}
                  onChange={(e) => setUrlText(e.target.value)}
                />
                <span className="fw-f__hint">
                  글꼴 파일 주소(<code>.woff2</code> · <code>.woff</code> · <code>.ttf</code> · <code>.otf</code>) 또는
                  배포처가 준 <code>@font-face</code> CSS 주소를 넣으세요 — CSS 면 그 안의 글꼴 주소를 대신 읽어 옵니다.
                </span>
              </div>
              <div className="fw-urlrow">
                <button
                  type="button"
                  className="um-btn um-btn--primary um-btn--sm"
                  disabled={reading || urlText.trim() === ''}
                  onClick={() => void probeUrls()}
                >
                  {reading ? <Loader2 size={14} className="fw-spin" /> : <Upload size={14} />}
                  가져와 확인
                </button>
                <span className="fw-sec__d">서버가 대신 받아 형식과 용량을 검사합니다.</span>
              </div>
              <div className="fw-note fw-note--info">
                <Info size={15} />
                <span>
                  <b>왜 서버가 받나요?</b> 브라우저가 다른 도메인의 글꼴을 직접 받으면 대부분
                  <b> CORS 로 막힙니다.</b> 서버가 받아 <b>사본을 저장</b>하므로, 배포처가 주소를 내려도
                  회사 문서는 계속 같은 글꼴로 열립니다.
                </span>
              </div>
            </>
          )}

          {reading && <p className="fw-empty">읽는 중…</p>}

          {rows.length > 0 && (
            <div className="fw-sec">
              <div className="fw-sec__h">
                <h4>확인</h4>
                <span className="fw-sec__n">{rows.length}개</span>
                <span className="fw-sec__sp" />
                <button
                  type="button"
                  className="um-btn um-btn--sm"
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    for (const r of rows) next[r.key] = isSendable(r);
                    setPicked(next);
                  }}
                >
                  모두 선택
                </button>
                <button
                  type="button"
                  className="um-btn um-btn--sm"
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    for (const r of rows) next[r.key] = r.status === 'new';
                    setPicked(next);
                  }}
                >
                  신규만 선택
                </button>
                <button type="button" className="um-btn um-btn--sm" onClick={() => setAll([])}>
                  비우기
                </button>
              </div>
              <div className="fw-tw">
                <table className="fw-tbl fw-tbl--intake">
                  <thead>
                    <tr>
                      <th style={{ width: 34 }} />
                      <th>{tab === 'url' ? '주소' : '파일'}</th>
                      <th style={{ width: 160 }}>글꼴 이름</th>
                      <th style={{ width: 118 }}>굵기</th>
                      <th style={{ width: 92 }}>기울임</th>
                      <th style={{ width: 78 }}>용량</th>
                      <th style={{ width: 92 }}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const ok = isSendable(r);
                      const pill = PILL[r.status];
                      return (
                        <tr key={r.key} className={ok ? undefined : 'is-muted'}>
                          <td>
                            {ok && (
                              <input
                                className="fw-ck"
                                type="checkbox"
                                checked={picked[r.key] === true}
                                aria-label={`${r.name} 등록 대상`}
                                onChange={(e) => setPicked((p) => ({ ...p, [r.key]: e.target.checked }))}
                              />
                            )}
                          </td>
                          <td>
                            <div className="fw-path">
                              {r.path && <span className="fw-path__dir">{r.path}</span>}
                              <b>{r.name}</b>
                              {r.reason && (
                                <span className={r.status === 'skip' || r.status === 'dupe' ? 'fw-why' : 'fw-why fw-why--bad'}>
                                  {r.reason}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            {ok ? (
                              <input
                                className="um-inp fw-inp--sm"
                                value={r.family}
                                maxLength={80}
                                aria-label="글꼴 이름"
                                placeholder={presetFamily || '예: 맑은 고딕'}
                                onChange={(e) => patchRow(r.key, { family: e.target.value })}
                              />
                            ) : <span className="fw-dash">-</span>}
                          </td>
                          <td>
                            {ok ? (
                              <div className="um-selw fw-selw">
                                <select
                                  className="um-inp fw-inp--sm"
                                  value={r.weight}
                                  aria-label="굵기"
                                  onChange={(e) => patchRow(r.key, { weight: Number(e.target.value) })}
                                >
                                  {WEIGHT_OPTIONS.map((w) => (
                                    <option key={w} value={w}>{w} · {WEIGHT_LABEL[w]}</option>
                                  ))}
                                </select>
                              </div>
                            ) : null}
                          </td>
                          <td>
                            {ok ? (
                              <div className="um-selw fw-selw">
                                <select
                                  className="um-inp fw-inp--sm"
                                  value={r.style}
                                  aria-label="기울임"
                                  onChange={(e) => patchRow(r.key, { style: e.target.value as 'normal' | 'italic' })}
                                >
                                  <option value="normal">보통</option>
                                  <option value="italic">기울임</option>
                                </select>
                              </div>
                            ) : null}
                          </td>
                          <td className="num">{r.sizeBytes ? formatBytes(r.sizeBytes) : '-'}</td>
                          <td><span className={`fw-pill ${pill.cls}`}>{pill.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="fw-modal__f">
          {running ? (
            <span className="fw-progwrap">
              <span className="fw-prog"><i style={{ width: `${sendable.length ? (done / sendable.length) * 100 : 0}%` }} /></span>
              <span className="fw-cnt">{done} / {sendable.length} 등록 중…</span>
            </span>
          ) : (
            <span className="fw-cnt">
              {rows.length === 0
                ? '올릴 글꼴을 고르세요'
                : <><b>{sendable.length}벌</b> 등록{replaceN > 0 && ` (교체 ${replaceN})`}{excluded > 0 && ` · ${excluded}개 제외`}</>}
            </span>
          )}
          <span className="fw-modal__sp" />
          <button type="button" className="um-btn" disabled={running} onClick={onClose}>취소</button>
          <button
            type="button"
            className="um-btn um-btn--primary"
            disabled={running || sendable.length === 0}
            onClick={() => void run()}
          >
            {running ? <Loader2 size={14} className="fw-spin" /> : <Plus size={14} />} 등록
          </button>
        </div>
      </div>
    </div>
  );
}
